import React from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';

import MenuItem from 'flow/components/ui-components/MenuItem';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectAssignee,
    selectOwnUserIsMemberOfTicket,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { updateTicketAssignee } from 'flow/actions/tickets-action';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { segmentTrack } from 'flow/actions/segment-actions';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import useTicketAction from 'flow/hooks/useTicketAction';

type Props = {
    ticketKey: TicketKey;
};

const MenuItemAssignToMe = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();
    const { openModal } = useModal();

    const assignee = useAppSelector((state) =>
        selectAssignee(state, ticketKey),
    );
    const canAssign = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'assign'),
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const ownUserIsTicketMember = useAppSelector((state) =>
        selectOwnUserIsMemberOfTicket(state, ticketKey),
    );

    const menuItemShouldBeDisplayed =
        assignee !== ownExtensionKey && ownUserIsTicketMember;

    if (!menuItemShouldBeDisplayed) {
        return null;
    }

    const onClickAssignToMe = (event?: React.MouseEvent) => {
        event?.stopPropagation();
        if (!canAssign) {
            return;
        }

        dispatch(
            segmentTrack(TICKET_STRINGS.ASSIGNED_TO_ME, {
                component: 'ticket context menu',
            }),
        );

        runTicketAction(updateTicketAssignee(ticketKey, ownExtensionKey)).catch(
            (error: any) => {
                if (error.status === 404) {
                    openModal('confirmationDialog', {
                        title: i18next.t('tickets.eligible.title'),
                        description: i18next.t('tickets.eligible.ccpro'),
                        handleAccept: () => {},
                        hideCancelButton: true,
                    });
                }
            },
        );
    };

    return (
        <MenuItem
            disabled={!canAssign}
            tooltipLabel={
                canAssign ? undefined : i18next.t('tickets.no_permission')
            }
            onClick={onClickAssignToMe}
        >
            <span>{i18next.t('ticket.assign.to.me')}</span>
        </MenuItem>
    );
};

export default MenuItemAssignToMe;
